<template>
  <div>
    <service-category-form
      v-model="formData.service_category_id"
    ></service-category-form>
    <divider></divider>
    <eligible-technicians-form
      v-model="formData.user_id"
      :service-category-id="formData.service_category_id"
      @update-pinned-reason="setPinnedReason"
    ></eligible-technicians-form>
    <divider></divider>
    <availability-form
      v-if="formData.service_category_id"
      ref="availabilityForm"
      :scheduled-time="formData.scheduled_time"
      :arrival_window_start="formData.arrival_window_start"
      :arrival_window_end="formData.arrival_window_end"
      :service-category-id="formData.service_category_id"
      :technician-id="formData.user_id"
      :timezone="address.time_zone"
      :postal-code="address.zip_code"
      :current-scheduled-time="formData.scheduled_time"
      :default-day="defaultDay"
      data-vv-name="Scheduled"
      allow-do-not-notify
      @unavailable="setUnavailable"
      @do-not-notify="setDoNotNotify"
      @update:data="updateData"
    ></availability-form>
    <div v-else>
      <b>Scheduled:</b>
      <span class="hint-text"
        >Select a Service Category to View Availability</span
      >
    </div>
    <divider></divider>
    <text-area
      v-model="formData.notes"
      label="Appointment Notes"
      class="full-width"
    ></text-area>
  </div>
</template>

<script setup lang="ts">
import moment from "moment-timezone";
import { computed, defineExpose, defineProps, ref } from "vue";

import Divider from "@/components/Divider.vue";
import TextArea from "@/components/TextArea.vue";
import AvailabilityForm from "@/EditAppointmentModal/forms/AvailabilityForm.vue";
import { AvailabilityFormInterface } from "@/EditAppointmentModal/forms/AvailabilityForm.vue";
import EligibleTechniciansForm from "@/EditAppointmentModal/forms/EligibleTechniciansForm.vue";
import ServiceCategoryForm from "@/EditAppointmentModal/forms/ServiceCategoryForm.vue";
import useVuelidate from "@vuelidate/core";
import { required } from "@vuelidate/validators";

type Address = {
  time_zone?: string;
  zip_code?: string;
};

const defaultDay = computed(() => moment().format("YYYY-MM-DD"));

const availabilityForm = ref<AvailabilityFormInterface | null>(null);

const props = withDefaults(
  defineProps<{
    address?: Address;
  }>(),
  {
    address: () => ({})
  }
);

const initialData = () => {
  return {
    formData: {
      service_category_id: undefined,
      scheduled_time: "",
      arrival_window_start: "",
      arrival_window_end: "",
      user_id: null,
      notes: "",
      unavailable_time: false,
      pinned_reason: "",
      skip_notifications: false
    }
  };
};

const formData = ref({
  service_category_id: undefined,
  scheduled_time: "",
  arrival_window_start: "",
  arrival_window_end: "",
  user_id: null,
  notes: "",
  unavailable_time: false,
  pinned_reason: "",
  skip_notifications: false
});

const rules = {
  service_category_id: { required }
};

const v$ = useVuelidate(rules, formData);

const getFormData = () => {
  return formData.value;
};

const resetFormData = () => {
  formData.value = initialData().formData;
};

const setUnavailable = (unavailable) => {
  formData.value.unavailable_time = unavailable;
};

const setDoNotNotify = (doNotNotify) => {
  formData.value.skip_notifications = doNotNotify;
};

const setPinnedReason = (val) => {
  formData.value.pinned_reason = val;
};

const updateData = ({ key, value }) => {
  formData.value[key] = value;
};

const validate = async () => {
  const isValidForm = await v$.value.$validate();
  const isValidAvailability = await availabilityForm.value?.validate();

  return isValidForm && isValidAvailability;
};

defineExpose({
  validate,
  getFormData,
  resetFormData
});
</script>

<style lang="scss" scoped>
/* BOLDING TEXTAREA LABEL */
:deep(.label) {
  font-weight: bold;
}
</style>
